import { useTranslation } from "@bounce/i18n"
import { getGmbAddress } from "@bounce/store"
import type { Nullable } from "@bounce/util"

import type { MainStoreFragment, Maybe } from "../libs/bounce/bounceSdk.types.gen"

export type StoreContact = {
  name: string
  alternateName: string
  fullAddress: string
  streetAddress: string
  addressLocality: string
  postalCode: string
  addressRegion: string | null
  addressCountry: string
  telephone: string | null
  latitude: number
  longitude: number
}

type UseStoreContactOptions = {
  mainStore?: Maybe<MainStoreFragment>
  place: string
}

/**
 * Get the store contact information.
 * @example
 * getStoreContact({ mainStore: { nickname: "Bounce", address: { street: "1 Starcross St", zipCode: "NW1 2HR" }, city: { name: "London", state: "Greater London", country: "United Kingdom" }, gmbPhone: "1234567890", coordinates: { latitude: 51.524, longitude: -0.127 } }, showGmbAddress: true })
 * // { name: "Bounce", fullAddress: "1 Starcross St, London NW1 2HR, Greater London, United Kingdom", streetAddress: "1 Starcross St", addressLocality: "London", postalCode: "NW1 2HR", addressRegion: "Greater London", addressCountry: "United Kingdom", telephone: "1234567890", latitude: 51.524, longitude: -0.127, gmbAddress: true }
 */
export const useStoreContact = ({ mainStore, place }: UseStoreContactOptions): Nullable<StoreContact> => {
  const { t } = useTranslation()

  if (!mainStore?.gmbAddress) {
    return null
  }

  const { address, city, gmbAddress, gmbPhone, coordinates, gmbLocationName } = mainStore
  const { addressLines, locality, postalCode, administrativeArea, regionCode } = gmbAddress
  const fullGmbAddress = getGmbAddress(mainStore.gmbAddress)

  return {
    name: gmbLocationName,
    alternateName: t("hook.useStoreContact.alternateName", "{{place}} Luggage Storage", { place }),
    fullAddress: fullGmbAddress,
    streetAddress: addressLines,
    addressLocality: locality || city.name,
    postalCode: postalCode || address.zipCode,
    addressRegion: administrativeArea,
    addressCountry: regionCode,
    telephone: gmbPhone,
    latitude: coordinates.latitude,
    longitude: coordinates.longitude,
  }
}
